import type { MemberDataFirestoreRecord } from "@albertine/types";
import { addDays, differenceInDays, subHours } from "date-fns";
import { millisecondsInHour } from "date-fns/constants";
import { z } from "zod";
import { isAppVersionLower } from "../../utils/app.util";
import { IsoDateTimeString, MemberId } from "../validate";

export const ReferralPromotionPopupCharacterLimit = 260;
export const ReferralPromotionNotificationCharacterLimit = 150;
export const ReferralPromotionLengthInDays = 14;
/*
 * ISO 8601 date format with timezone offset
 * Example: 2023-10-05T14:48:00Z
 */
export const ReferralPromotionDateFormat = IsoDateTimeString;
export type ReferralPromotionDateFormat = z.infer<typeof ReferralPromotionDateFormat>;

const ReferralPromotionId = z.uuid();
const ReferralNominationId = z.uuid();

export const Nomination = z.object({
    id: ReferralNominationId,
    firstName: z.string().max(256),
    lastName: z.string().max(256),
    phoneNumber: z.string().max(256),
    createdAt: ReferralPromotionDateFormat,
});

export type Nomination = z.infer<typeof Nomination>;

const ReferralPromotionStatus = z.enum(["active", "successful", "unsuccessful"]);

const PromotionPopup = z.object({
    description: z
        .string({ error: "Popup content is required" })
        .min(1, "Popup content is required")
        .max(ReferralPromotionPopupCharacterLimit),
});

const PromotionNotification = z.object({
    description: z
        .string({ error: "Notification content is required" })
        .min(1, "Notification content is required")
        .max(ReferralPromotionNotificationCharacterLimit),
});

export const ReferralPromotion = z.object({
    id: ReferralPromotionId,
    memberId: MemberId,
    createdAt: ReferralPromotionDateFormat,
    startTime: ReferralPromotionDateFormat,
    endTime: ReferralPromotionDateFormat,
    popupContent: PromotionPopup,
    notificationContent: PromotionNotification,
});

export const ReferralPromotionWithNomination = ReferralPromotion.extend(
    z.object({
        nomination: Nomination.nullable(),
    }).shape,
);

export const MemberReferralPromotion = ReferralPromotion.pick({
    id: true,
    startTime: true,
    endTime: true,
    popupContent: true,
});

export const ReferralPromotionSchedule = ReferralPromotion.pick({
    startTime: true,
    endTime: true,
}).extend(
    z.object({
        numberOfNotificationsInBetween: z.number(),
    }).shape,
);

export const ReferralPromotionNotificationSchedule = z.array(
    z.object({
        time: ReferralPromotionDateFormat,
        notificationTitle: z.string(),
    }),
);
type ReferralPromotionNotificationSchedule = z.infer<
    typeof ReferralPromotionNotificationSchedule
>;

export const ReferralPromotionWithStatus = z.object({
    status: ReferralPromotionStatus,
    promotion: ReferralPromotionWithNomination,
});

export type ReferralPromotion = z.infer<typeof ReferralPromotion>;
export type ReferralPromotionWithNomination = z.infer<
    typeof ReferralPromotionWithNomination
>;
export type MemberReferralPromotion = z.infer<typeof MemberReferralPromotion>;
export type ReferralPromotionSchedule = z.infer<typeof ReferralPromotionSchedule>;
export type ReferralPromotionWithStatus = z.infer<typeof ReferralPromotionWithStatus>;

export const CreateReferralPromotionSchema = z.object({
    popupContent: PromotionPopup,
    notificationContent: PromotionNotification,
});

export type CreateReferralPromotionSchema = z.infer<
    typeof CreateReferralPromotionSchema
>;

export const CreateNomimationSchema = Nomination.pick({
    firstName: true,
    lastName: true,
    phoneNumber: true,
});

export type CreateNomimationSchema = z.infer<typeof CreateNomimationSchema>;

export const MemberReferralsAPIResponse = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("nominate-member"),
        nomination: ReferralNominationId.or(z.null()),
    }),
    z.object({
        action: z.literal("get-referral-promotion"),
        promotion: MemberReferralPromotion.or(z.null()),
    }),
]);

export type MemberReferralsAPIResponse = z.infer<typeof MemberReferralsAPIResponse>;

export const MemberReferralsAPIRequest = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("nominate-member"),
        promotionId: ReferralPromotionId,
        nomination: CreateNomimationSchema,
    }),
    z.object({
        action: z.literal("get-referral-promotion"),
    }),
]);

export type MemberReferralsAPIRequest = z.infer<typeof MemberReferralsAPIRequest>;

export const ReferralNominationDbSchema = z.object({
    id: ReferralNominationId,
    promotion_id: ReferralPromotionId,
    details: CreateNomimationSchema,
    created_at: z.date(),
});

export type ReferralNominationDbSchema = z.infer<typeof ReferralNominationDbSchema>;

export const ReferralPromotionDbSchema = z.object({
    id: ReferralPromotionId,
    member_id: MemberId,
    start_time: z.date(),
    end_time: z.date(),
    popup_description: z.string().max(ReferralPromotionPopupCharacterLimit),
    notification_description: z
        .string()
        .max(ReferralPromotionNotificationCharacterLimit),
    created_at: z.date(),
    updated_at: z.date(),
});

export type ReferralPromotionDbSchema = z.infer<typeof ReferralPromotionDbSchema>;

export const ReferralPromotionLeftJoinNominationsDbSchema =
    ReferralPromotionDbSchema.extend(
        z.object({
            nomination_id: ReferralNominationId.nullable(),
            nomination_details: CreateNomimationSchema.nullable(),
            nomination_created_at: z.date().nullable(),
        }).shape,
    );

export type ReferralPromotionLeftJoinNominationsDbSchema = z.infer<
    typeof ReferralPromotionLeftJoinNominationsDbSchema
>;

export function isEligibleForReferrals(member: MemberDataFirestoreRecord): boolean {
    return (
        member.tenant === "albertine" &&
        member.appVersion != null &&
        member.appVersion !== "" &&
        !isAppVersionLower(member.appVersion, "1.5.24")
    );
}

function isActivePromotion(
    promotion: ReferralPromotionWithNomination,
    now: Date = new Date(),
) {
    if (promotion.nomination) {
        return false;
    }

    return new Date(promotion.startTime) <= now && new Date(promotion.endTime) >= now;
}

function isSuccessfulPromotion(promotion: ReferralPromotionWithNomination) {
    return promotion.nomination !== null;
}

export function groupReferralPromotions(
    promotions: ReferralPromotionWithNomination[],
    now: Date = new Date(),
): ReferralPromotionWithStatus[] {
    return promotions.map((promotion) => {
        if (isActivePromotion(promotion, now)) {
            return {
                status: "active",
                promotion,
            };
        }

        if (isSuccessfulPromotion(promotion)) {
            return {
                status: "successful",
                promotion,
            };
        }

        return {
            status: "unsuccessful",
            promotion,
        };
    });
}

/**
 * Returns schedule for the referral promotions and the amount of extra notifications sent.
 */
export function getReferralPromotionSchedule(
    now: Date = new Date(),
): ReferralPromotionSchedule {
    const promotionLengthInDays = addDays(now, ReferralPromotionLengthInDays);

    return {
        startTime: now.toISOString(),
        endTime: promotionLengthInDays.toISOString(),
        numberOfNotificationsInBetween: 1,
    };
}
/**
 * Returns schedule for the referral promotions and their notifications.
 */
export function getNotificationSchedule(promotionSchedule: ReferralPromotionSchedule) {
    const lastNotificationTime = subHours(
        new Date(promotionSchedule.endTime),
        24,
    ).toISOString();
    const daysInBetweenInitialAndLastNotification = differenceInDays(
        new Date(promotionSchedule.endTime),
        new Date(promotionSchedule.startTime),
    );
    const numberOfNotificationPeriods =
        promotionSchedule.numberOfNotificationsInBetween + 1;
    const daysInBetweenNotifications =
        daysInBetweenInitialAndLastNotification / numberOfNotificationPeriods;

    const notificationsBetweenInitialAndLast = Array.from({
        length: promotionSchedule.numberOfNotificationsInBetween,
    }).map((_, index) => {
        const periodFactor = index + 1;
        const time = addDays(
            new Date(promotionSchedule.startTime),
            daysInBetweenNotifications * periodFactor,
        );

        return {
            time: time.toISOString(),
            daysUntilPromotionEnd: differenceInDays(
                new Date(promotionSchedule.endTime),
                time,
            ),
        };
    });

    return {
        initialNotificationTime: promotionSchedule.startTime,
        lastNotificationTime,
        notificationsBetweenInitialAndLast,
    };
}

export function referralPromotionExpiresInDays(
    endDate: Date | null,
    now: Date = new Date(),
): number {
    const msInDay = 24 * millisecondsInHour;
    const expiresInDays = endDate
        ? Math.ceil((endDate.getTime() - now.getTime()) / msInDay)
        : ReferralPromotionLengthInDays;
    const expiresInDaysClamped = Math.max(0, expiresInDays);
    return expiresInDaysClamped;
}
